import type { IconSize } from '../../../../../../../mads/icons/Icon';

import { Box, Button, Divider, Flex, Text } from '../../../../../../../mads';
import { Check, Close, Question } from '../../../../../../../mads/icons';
import { useI18n } from '../../../../../../../shared/I18n';

import { colors } from '@indeed/ifl-tokens';
import { borderRadius } from '@indeed/ifl-tokens/react-native';
import React from 'react';
import { StyleSheet } from 'react-native';

/**
 * Variants for MatchQualification component
 * - Matching: Green check icon, indicates the user meets this qualification
 * - Missing: Orange question icon, indicates the qualification is not confirmed
 * - Mismatching: Red ban icon, indicates the user does not meet this qualification
 */
export enum MatchQualificationVariant {
    Matching = 'matching',
    Missing = 'missing',
    Mismatching = 'mismatching',
}

/**
 * Props for MatchQualification component
 */
export interface MatchQualificationProps {
    /** The variant determining the icon and styling */
    variant: MatchQualificationVariant;
    /** Main qualification text */
    primaryText: string;
    /** Whether the qualification is required */
    required: boolean;
    /** Optional custom CTA text (defaults to "Add") */
    ctaText?: string;
    /** Optional callback when Confirm is pressed */
    onConfirm: () => void;
    /** Optional callback when Reject is pressed */
    onReject: () => void;
    /** Optional callback when Undo is pressed */
    onUndo?: () => void;
}

interface VariantConfig {
    icon: React.ComponentType<{ color: string; size: IconSize }>;
    iconColor: string;
    backgroundColor: string;
    textColor?: string;
    iconAriaLabel: string;
}

const getVariantConfigs: (
    gt: ReturnType<typeof useI18n>,
) => Record<MatchQualificationVariant, VariantConfig> = (gt: ReturnType<typeof useI18n>) => ({
    [MatchQualificationVariant.Matching]: {
        icon: Check,
        iconColor: colors.foreground.status.success['[base]'],
        backgroundColor: colors.background.status.success.weaker,
        textColor: colors.foreground.status.success['[base]'],
        iconAriaLabel: gt.gettext('Matching qualification'),
    },
    [MatchQualificationVariant.Missing]: {
        icon: Question,
        iconColor: colors.foreground['[base]'],
        backgroundColor: colors.background.weak,
        textColor: colors.foreground['[base]'],
        iconAriaLabel: gt.gettext('Missing qualification'),
    },
    [MatchQualificationVariant.Mismatching]: {
        icon: Close,
        iconColor: colors.foreground.status.warning['[base]'],
        backgroundColor: colors.background.status.warning.weaker,
        textColor: colors.foreground.status.warning['[base]'],
        iconAriaLabel: gt.gettext('Mismatching qualification'),
    },
});

export const MatchQualification: React.FC<MatchQualificationProps> = ({
    variant,
    primaryText,
    required,
    onConfirm,
    onReject,
    onUndo,
}) => {
    const gt = useI18n();
    const variantConfigs = getVariantConfigs(gt);
    const config = variantConfigs[variant];
    const IconComponent = config.icon;

    return (
        <Flex
            sx={{
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
                marginBlockStart: 8,
                flexWrap: 'nowrap',
                minHeight: 32,
            }}
        >
            <Box
                accessible={true}
                accessibilityLabel={config.iconAriaLabel}
                accessibilityRole="image"
                sx={{
                    width: 24,
                    height: 24,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    flexBasis: 24,
                    backgroundColor: config.backgroundColor,
                    borderRadius: 8,
                }}
            >
                <IconComponent color={config.iconColor} size="xs" />
            </Box>

            <Text
                level={2}
                sx={{
                    flexWrap: 'wrap',
                    flexGrow: 1,
                    flexBasis: 0,
                }}
            >
                <Text
                    sx={{
                        color: config.textColor,
                        fontWeight: '500',
                    }}
                >
                    {primaryText}
                </Text>
                <Text
                    sx={{
                        color: config.textColor,
                        fontStyle: 'italic',
                    }}
                >
                    {required && ` (${gt.gettext('required')})`}
                </Text>
                {onUndo && (
                    <>
                        {'\n'}
                        <Text level={1}>{gt.gettext('Saved to your profile.')}</Text>
                    </>
                )}
            </Text>

            {variant === MatchQualificationVariant.Missing && !onUndo && (
                <Flex sx={styles.buttonGroup}>
                    <Button
                        variant="tertiary"
                        onPress={onConfirm}
                        ariaLabel={gt.gettext('Confirm qualification')}
                        iconAfter={
                            <Check size="xs" fill={colors.background.status.success['[base]']} />
                        }
                        iconOnly
                    />
                    <Divider vertical />
                    <Button
                        variant="destructiveTertiary"
                        onPress={onReject}
                        ariaLabel={gt.gettext('Reject qualification')}
                        iconAfter={
                            <Close
                                size="xs"
                                fill={colors.background.action.destructive['[base]']}
                            />
                        }
                        iconOnly
                    />
                </Flex>
            )}
            {onUndo && (
                <Button variant="tertiary" onPress={onUndo}>
                    {gt.gettext('Undo')}
                </Button>
            )}
        </Flex>
    );
};

const styles = StyleSheet.create({
    buttonGroup: {
        flexDirection: 'row',
        flexShrink: 0,
        flexBasis: 78,
        alignSelf: 'flex-end',
        borderWidth: 1,
        borderColor: colors.border.weak,
        borderRadius: borderRadius.md,
    },
});
